/**
 * Platform service cost tracking — what Intrlock pays for each service.
 * Used to calculate margins and profitability.
 */

export interface ServiceCost {
  id: string;
  name: string;
  provider: string;
  category: string;
  cost_type: 'per_unit' | 'per_minute' | 'flat_monthly' | 'percentage';
  cost_amount: number;       // cents for per_unit/per_minute, dollars for flat_monthly, basis points for percentage
  cost_label: string;        // human-readable cost description
  api_proxy_url: string;     // masked proxy URL (not the real endpoint)
  active_count?: number;     // populated at runtime
  monthly_cost?: number;     // populated at runtime
  monthly_revenue?: number;  // populated at runtime
  margin_pct?: number;       // populated at runtime
}

// Hardcoded platform services — these represent real costs to Intrlock
export const PLATFORM_SERVICES: ServiceCost[] = [
  {
    id: 'telnyx_numbers',
    name: 'Phone Numbers',
    provider: 'Telnyx',
    category: 'Voice',
    cost_type: 'per_unit',
    cost_amount: 100, // $1.00/number/mo
    cost_label: '$1.00/number/mo',
    api_proxy_url: '/api/voice/numbers',
  },
  {
    id: 'telnyx_calling',
    name: 'Voice Calling',
    provider: 'Telnyx',
    category: 'Voice',
    cost_type: 'per_minute',
    cost_amount: 1, // $0.01/min
    cost_label: '$0.01/min',
    api_proxy_url: '/api/voice/calls',
  },
  {
    id: 'livekit_video',
    name: 'Video Calling',
    provider: 'LiveKit',
    category: 'Video',
    cost_type: 'per_minute',
    cost_amount: 4, // $0.04/participant-min
    cost_label: '$0.04/participant-min',
    api_proxy_url: '/api/video/rooms',
  },
  {
    id: 'aws_lambda',
    name: 'Serverless Functions',
    provider: 'AWS Lambda',
    category: 'Infrastructure',
    cost_type: 'flat_monthly',
    cost_amount: 25, // ~$25/mo estimated
    cost_label: '~$25/mo',
    api_proxy_url: '/api/lambda',
  },
  {
    id: 'aws_s3_cloudfront',
    name: 'Storage & CDN',
    provider: 'AWS S3 + CloudFront',
    category: 'Infrastructure',
    cost_type: 'flat_monthly',
    cost_amount: 15,
    cost_label: '~$15/mo',
    api_proxy_url: '/api/storage',
  },
  {
    id: 'aws_api_gateway',
    name: 'API Gateway',
    provider: 'AWS API Gateway',
    category: 'Infrastructure',
    cost_type: 'flat_monthly',
    cost_amount: 10,
    cost_label: '~$10/mo',
    api_proxy_url: '/api/gateway',
  },
  {
    id: 'pdk_access',
    name: 'Access Control API',
    provider: 'PDK',
    category: 'Access Control',
    cost_type: 'flat_monthly',
    cost_amount: 0, // included in PDK dealer agreement
    cost_label: 'Included (dealer)',
    api_proxy_url: '/api/pdk',
  },
  {
    id: 'brivo_access',
    name: 'Brivo Access Control',
    provider: 'Brivo',
    category: 'Access Control',
    cost_type: 'flat_monthly',
    cost_amount: 0,
    cost_label: 'Included (dealer)',
    api_proxy_url: '/api/brivo',
  },
  {
    id: 'stripe_processing',
    name: 'Payment Processing',
    provider: 'Stripe',
    category: 'Payments',
    cost_type: 'percentage',
    cost_amount: 290, // 2.9% = 290 basis points
    cost_label: '2.9% + $0.30/txn',
    api_proxy_url: '/api/stripe',
  },
  {
    id: 'aws_rekognition',
    name: 'Facial Recognition',
    provider: 'AWS Rekognition',
    category: 'AI',
    cost_type: 'per_unit',
    cost_amount: 1, // $0.01/face search
    cost_label: '$0.01/search',
    api_proxy_url: '/api/face-verify',
  },
];

/**
 * Calculate costs, revenue, and margins for all services
 */
export function calculateServiceMetrics(
  services: ServiceCost[],
  activeData: {
    phoneNumbers: number;
    talkMinutes: number;
    videoMinutes: number;
    totalRevenue: number;   // monthly revenue in cents
    transactionCount: number;
  }
): ServiceCost[] {
  return services.map(s => {
    let monthlyCost = 0;
    let activeCount = 0;

    switch (s.id) {
      case 'telnyx_numbers':
        activeCount = activeData.phoneNumbers;
        monthlyCost = activeCount * (s.cost_amount / 100);
        break;
      case 'telnyx_calling':
        activeCount = activeData.talkMinutes;
        monthlyCost = activeCount * (s.cost_amount / 100);
        break;
      case 'livekit_video':
        activeCount = activeData.videoMinutes;
        monthlyCost = activeCount * (s.cost_amount / 100);
        break;
      case 'stripe_processing':
        monthlyCost = (activeData.totalRevenue / 100) * (s.cost_amount / 10000) + (activeData.transactionCount * 0.30);
        break;
      default:
        if (s.cost_type === 'flat_monthly') {
          monthlyCost = s.cost_amount;
        }
    }

    const monthlyRevenue = activeData.totalRevenue / 100; // total platform revenue
    const marginPct = monthlyRevenue > 0 ? ((monthlyRevenue - monthlyCost) / monthlyRevenue) * 100 : 0;

    return {
      ...s,
      active_count: activeCount,
      monthly_cost: monthlyCost,
      monthly_revenue: monthlyRevenue,
      margin_pct: marginPct,
    };
  });
}

/**
 * Get total monthly platform costs
 */
export function getTotalMonthlyCost(services: ServiceCost[]): number {
  return services.reduce((sum, s) => sum + (s.monthly_cost || 0), 0);
}
